import allSps from '@api-consent/defs/spsRaw';
import { TefComs } from '@mfe/types/typings';
import { createTestBearerToken, isCorrectUrlForWebCrypto } from '@mfec/testing';
import type Vue from 'vue';

import { components } from './components';

const spIds = allSps
  .map((sp) => ({ label: `${sp.id} (${sp.title})`, value: sp.id }))
  .sort((a, b) => (a.value < b.value ? -1 : 1));

const shortcuts = {
  apiUrlGateway: [
    { value: 'https://ict.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'ict' },
    { value: 'https://e2e2.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'e2e2' },
    { value: 'https://e2e1.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'e2e1' },
    { value: 'https://prod.high.apigw-prod.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'prod' },
    { value: 'https://api-consent-coms.telefonica.de/gateway/4p-extra-services/v1', label: 'prod/extern' },
  ],
  apiUrlDirect: [
    { value: 'http://localhost:3200', label: 'api4p@localhost' },
    { value: 'http://localhost:8080', label: 'wiremock@localhost' },
    { value: 'https://ict-api-4p.high.coms-dev.aws.de.pri.o2.com', label: 'ict' },
    { value: 'https://e2e2-api-4p.high.coms-dev.aws.de.pri.o2.com', label: 'e2e2' },
    { value: 'https://e2e1-api-4p.high.coms-dev.aws.de.pri.o2.com', label: 'e2e1' },
  ],
  themeBaseUrl: [
    { value: 'https://dev-library.telefonica.de/1', label: 'occl/o2/dev' },
    { value: 'https://library.telefonica.de/1', label: 'occl/o2/prod' },
  ],
};

const search = new URLSearchParams(window.location.search);

let currentApiUrl = search.get('apiUrl');
if (!currentApiUrl) {
  if (window.location.protocol === 'http:' && /(127\.0\.0\.1|localhost)/.test(window.location.hostname)) {
    currentApiUrl = `http://${window.location.hostname}:3200`;
  } else {
    currentApiUrl = shortcuts.apiUrlGateway[0].value;
  }
}

const params = {
  accessToken: search.get('accessToken') ?? '',
  spId: search.get('spId') ?? 'SP901',
  lcId: search.get('lcId') ?? '4711',
  apiUrl: currentApiUrl,
  theme: search.get('theme') ?? 'occl',
  themeBaseUrl: search.get('themeBaseUrl') ?? shortcuts.themeBaseUrl[0].value,
  traceid: search.get('traceid') ?? '',
};

const createMicroFrontend = ({
  accessToken = '', spId = '', lcId = '', apiUrl = '', theme = 'occl', themeBaseUrl = '', traceid = '',
}: {
  accessToken?: string, spId: string, lcId: string, apiUrl: string,
  theme: string, themeBaseUrl: string, traceid?: string,
}) => {
  const mf = document.createElement('tef-coms4cs4p');
  const setAttributes = (attrs: Record<string, string | undefined>) => Object
    .entries(attrs)
    .filter(([, value]) => value) // ignore '', false, undefined
    .forEach(([name, value]) => mf.setAttribute(name, value!));
  setAttributes({
    id: 'consents',
    'access-token': accessToken,
    'sp-id': spId,
    'lc-id': lcId,
    'api-url': apiUrl,
    theme,
    'theme-base-url': themeBaseUrl,
    traceid,
  });
  document.getElementById('mf')?.replaceChildren(mf);
};

// @ts-ignore
const app = new Vue({
  el: '#app',

  components,

  data: {
    // definitions
    spIds,
    shortcuts,
    // attributes
    ...params,
    // runtime props
    ready: false,
    info: 'Messages:',
    error: false as boolean | string | Error,
  },

  created() {
    this.init();
  },

  methods: {
    init: () => {
      document.addEventListener('tef-coms4cs4p', (event: Event) => {
        const { detail } = event as TefComs.Event;
        const { name, level, error } = detail ?? {};
        console.log('[tef-coms4cs4p] Received \'%s\' event with details %o', name, detail);
        switch (name) {
          case 'load/READY':
            app.ready = true;
            app.setInfo('load/READY: The micro-frontend is ready.');
            break;
          case 'consents/EMPTY':
            app.setInfo('consents/EMPTY: No consents found for this customer');
            break;
          case 'consents/SAVE':
            app.setInfo('consents/SAVE: Consents saved sucessfully');
            break;
          case 'action/CANCEL':
            app.setInfo('action/CANCEL: CANCEL button pressed');
            break;
          default:
        }

        if (level === 'error' || error) {
          app.setError(error ?? detail.message ?? 'an error occurred');
        }
      });
    },

    reload: () => {
      const query = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        query.set(key, app.$data[key] as string ?? '');
      });
      window.location.href = `${window.location.pathname}?${query.toString()}`; // reload page
    },

    create: () => {
      try {
        createMicroFrontend(app);
      } catch (error) {
        app.setError(error as Error);
      }
    },

    showHtml: () => {
      const escapeHtml = (unsafe: string) => unsafe
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
      const html = document.getElementById('consents')?.outerHTML;
      app.setInfo(html ? escapeHtml(html) : 'Element not found.');
    },

    setTestToken: async () => {
      if (!isCorrectUrlForWebCrypto()) {
        app.setError('Creating a token only works within a secure context. Please use "localhost" as a hostname or https to access this page.');
        return;
      }
      app.accessToken = await createTestBearerToken();
    },

    setInfo(text: string) {
      app.info = [app.info].concat(text).join('<br />');
      app.error = false;
    },

    setError(error: Error | string) {
      this.error = error;
      if (error instanceof Error) {
        const { message, stack } = error;
        const stackText = stack ? `<br />${stack}` : '';
        this.info = `${message}${stackText}`;
      } else {
        this.info = error;
      }
    },

    setValue<T = unknown>(prop: string, value: T) {
      app.$data[prop] = value;
    },
  },
});

app.create();
